// @mui
import { styled } from '@mui/material/styles'
import { Chip, Typography, Stack, Button } from '@mui/material'
// components
import Iconify from '@/components/Iconify'
import find from 'lodash/find'
import uniq from 'lodash/uniq'
import { TagsForAllProductsFragment } from '@/generated/graphql'

// ----------------------------------------------------------------------

const RootStyle = styled('div')({
  flexGrow: 1,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
})

const WrapperStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  overflow: 'hidden',
  alignItems: 'stretch',
  margin: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  border: `solid 1px ${theme.palette.divider}`,
}))

const LabelStyle = styled((props) => (
  <Typography component="span" variant="subtitle2" {...props} />
))(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.neutral,
  borderRight: `solid 1px ${theme.palette.divider}`,
}))

// ----------------------------------------------------------------------

type Props = {
  allSearchTags: TagsForAllProductsFragment[]
  filters: { tagIds: string[] }
  isShowReset?: boolean
  onResetAll: VoidFunction
  onRemoveTagId: (id: string) => void
}

export default function ShopTagFiltered({
  allSearchTags,
  filters,
  isShowReset = true,
  onRemoveTagId,
  onResetAll,
}: Props) {
  const { tagIds } = filters

  const handleRemoveTag = (tag?: TagsForAllProductsFragment) => {
    if (!tag) return
    const url = new URL(window.location.href)
    const tagsParamsString = url.searchParams.get('tags')
    const tagSearch = `${tag.sys.id}_${tag.name}`
    let tagsIdWithNameParams: string[] = []

    if (tagsParamsString) {
      tagsIdWithNameParams = tagsParamsString.split('|').filter((tag) => {
        const tagId = tag.split('_')[0]
        return allSearchTags.some((filterTag) => tagId === filterTag.sys.id)
      })
    }

    tagsIdWithNameParams = uniq(
      tagsIdWithNameParams.filter((tagIdWithName) => tagIdWithName !== tagSearch)
    )
    onRemoveTagId?.(tag.sys.id)
    if (tagsIdWithNameParams.length) {
      url.searchParams.set('tags', tagsIdWithNameParams.join('|'))
    } else {
      url.searchParams.delete('tags')
    }
    window.history.replaceState(null, '', url)
  }

  const handleResetAll = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete('tags')
    window.history.replaceState(null, '', url)
    onResetAll?.()
  }

  return (
    <RootStyle>
      {tagIds.length > 0 && (
        <WrapperStyle>
          <LabelStyle>Tag:</LabelStyle>
          <Stack direction="row" flexWrap="wrap" sx={{ p: 0.75 }}>
            {tagIds.map((tagId) => (
              <Chip
                key={tagId}
                label={find(allSearchTags, { sys: { id: tagId } })?.name}
                size="small"
                onDelete={() => handleRemoveTag(find(allSearchTags, { sys: { id: tagId } }))}
                sx={{ m: 0.5 }}
              />
            ))}
          </Stack>
        </WrapperStyle>
      )}

      {isShowReset && (
        <Button
          color="error"
          size="small"
          onClick={handleResetAll}
          startIcon={<Iconify icon={'ic:round-clear-all'} />}
        >
          Clear All
        </Button>
      )}
    </RootStyle>
  )
}
